/**
 * @FIXME(shawk):
 * Until an upstream dependency issue in "eslint-plugin-react" is resolved,
 * disabling eslint rule "react/prop-types" is the recommended approach
 * to resolve false positives in eslint.
 *
 * see:
 * - https://github.com/yannickcr/eslint-plugin-react/issues/3140
 * - https://github.com/redwoodjs/redwood/pull/3762
 */

import cx from 'classnames';
import React from 'react';
import { Collapse } from 'react-collapse';
import { FieldLabel, HelperText } from 'components/Forms';
import { RadioCardInput } from 'components/Forms/inputs/RadioCardInput';
import { ConnectedFormInputProps } from 'types/form-field';
import type { Option } from 'components/Forms';
import { RadioInputDot } from '../RadioCardInput/RadioInputDot';

export type Props = {
  /**
   * Stretch the radio buttons to fill the width of the container
   */
  fullWidth?: boolean;

  /**
   * Radio button options to display
   */
  options: Option[];

  /**
   * Render prop to show when options are empty. Is displayed between the label
   * and helper text/error message.
   */
  renderEmpty?: () => JSX.Element;

  /**
   * Disables the display of error messages in this component. Should be used only
   * if the error is otherwise displayed.
   */
  suppressError?: boolean;

  /**
   * Show a filled circle next to the selected option
   */
  showSelectedDot?: boolean;
} & ConnectedFormInputProps<HTMLInputElement>;

export const RadioCardGroup = React.forwardRef<HTMLInputElement, Props>(function RadioCardGroupWithRef(
  {
    name,
    label,
    className,
    accessibleLabel,
    error,
    hint,
    fullWidth = false,
    options,
    suppressError = false,
    showSelectedDot = false,
    renderEmpty,
    setError,
    setValue,
    clearValue,
    ...inputProps
  },
  ref?: React.ForwardedRef<HTMLInputElement>,
): JSX.Element {
  return (
    <fieldset>
      <FieldLabel as="legend" className="mb-16" accessibleLabel={accessibleLabel}>
        {label}
      </FieldLabel>

      <Collapse isOpened theme={{ collapse: 'transition-all duration-300' }}>
        <div
          className={cx(className, 'grid gap-8', {
            'grid-cols-auto-fit-[30%] sm:grid-cols-auto-fit-[15%]': !className && (fullWidth || options.length === 0),
            'grid-cols-3 sm:grid-cols-6': !className && (!fullWidth || options.length > 0),
          })}
        >
          {options.map(option => {
            const id = `${name}-${option.value}`;

            return (
              <RadioCardInput
                {...inputProps}
                ref={ref}
                id={id}
                key={id}
                defaultChecked={option.value === inputProps.value}
                name={name}
                option={
                  showSelectedDot
                    ? { ...option, startAdornment: checked => <RadioInputDot checked={checked} /> }
                    : option
                }
                hasError={!!error}
              />
            );
          })}

          {options.length === 0 && renderEmpty ? renderEmpty() : null}
        </div>
      </Collapse>

      {error && (
        <HelperText error className={cx({ 'sr-only': suppressError })}>
          {error}
        </HelperText>
      )}
      {hint && <HelperText>{hint}</HelperText>}
    </fieldset>
  );
});
